import { useState } from "react";
import type { ReactNode } from "react";
import { Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "@/components/ui/Button";
import { Modal } from "@/components/ui";
import { cn } from "@/lib/utils";

type ExpandableMapFrameProps = {
  /** Renders the map; `expanded` is true inside the near-fullscreen modal. */
  children: (expanded: boolean) => ReactNode;
  /** Outer chrome around the collapsed map (border, radius, fill height). */
  className?: string;
  /** Size of the collapsed map surface (e.g. h-48, lg:min-h-[280px]). */
  mapClassName?: string;
};

/**
 * Renders the map in either the inline slot or a near-fullscreen modal.
 * Children remount when toggling — Mapbox maps must recreate on the new container.
 */
export function ExpandableMapFrame({
  children,
  className,
  mapClassName,
}: ExpandableMapFrameProps) {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className={cn("relative overflow-hidden", className)}>
        <div className={cn("w-full", mapClassName)}>
          {expanded ? null : <div className="h-full w-full">{children(false)}</div>}
        </div>
        {expanded ? null : (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute left-2 top-2 z-10 border border-surface-200 bg-surface-0/95 text-surface-700 shadow-sm hover:bg-surface-0"
            onClick={() => setExpanded(true)}
            leftIcon={<Maximize2 className="h-4 w-4" />}
          >
            {t("map.expand")}
          </Button>
        )}
      </div>

      <Modal
        closeLabel={t("close")}
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        title={t("map.expandedTitle")}
        size="full"
        mobileFullScreen
        priority="high"
      >
        {/*
          Need a definite height for Mapbox (h-full children). min-h alone does
          not resolve percentage height → 0px canvas / white map (#757).
        */}
        <div className="-mx-4 -my-4 flex h-full min-h-0 flex-1 flex-col sm:-mx-6 sm:-my-5 sm:h-[min(70dvh,calc(100dvh-10rem))] sm:flex-none">
          <div className="h-full w-full">{children(true)}</div>
        </div>
      </Modal>
    </>
  );
}
